import { compact, filter, kebabCase, map } from 'lodash/fp';
import { ElementType } from 'react';
import { SCALE } from '@ahmdigital/constants';

import { ACCORDION_INITIAL_ITEMS_COUNT } from '../../../../../ahm-constants/ui';
import { CommonQuestions, Question } from '../../../../../types/__codegen__/sanity/graphql';
import { CONTENT_TYPE } from '../../../../../cms/constants';
import Award from '../../../../insurance/components/award';
import CommonQuestionsSection from '../../../../../components/sections/common-questions-section';
import ContentTilesSection from '../../../../../components/content-tiles-section';
import getContentTilesData from '../get-content-tiles-data';
import getFormattedQuestionForAccordion from '../../../../../utils/get-formatted-question-for-accordion';
import getImageAttributes from '../../../../../cms/utils/get-image-attributes';
import HowItWorks from '../../../../../components/how-it-works';
import Inclusions from '../../components/inclusions-section';
import PageSections from '../../../../consideration-landing/components/page-sections';
import ProductsSection from '../../../../../components/sections/products-section';
import QuickLinks from '../../../../../components/quick-links';
import Summaries from '../../components/summary-with-image-section';
import ValuePropositions from '../../components/value-propositions';
import type { ExplainedTermSection } from '../../types';

const getSectionContentToRender = (
  content: ExplainedTermSection[],
  customerScale?: (typeof SCALE)[keyof typeof SCALE],
) =>
  map((section) => {
    const contentType = section?.__typename;
    if (contentType === CONTENT_TYPE.HOW_TO) {
      return <HowItWorks howTo={section} />;
    }

    if (contentType === CONTENT_TYPE.VALUE_PROPOSITIONS) {
      return <ValuePropositions valuePropositions={section} />;
    }

    if (contentType === CONTENT_TYPE.COMMON_QUESTIONS) {
      // Type the GraphQL alias: the fragment returns 'summary' as an alias for 'summaryRaw' (see common-questions-fields fragment) AP-1892
      interface CommonQuestionsWithAlias extends CommonQuestions {
        summary?: CommonQuestions['summaryRaw'];
      }
      const commonQuestionsSection = section as CommonQuestionsWithAlias;
      const questions = compact(commonQuestionsSection.questions) as Question[];
      const questionsTitle = commonQuestionsSection.title ?? undefined;
      const questionsSummary = commonQuestionsSection.summary;
      const questionsImage = commonQuestionsSection.imageObject;
      const image = getImageAttributes({ imageObject: questionsImage });
      const formattedQuestions = getFormattedQuestionForAccordion(questions);

      return (
        <CommonQuestionsSection
          accordionId={`explained-term-common-questions-${kebabCase(questionsTitle ?? '')}`}
          image={image}
          questions={formattedQuestions}
          summary={questionsSummary}
          title={questionsTitle}
          initialVisible={ACCORDION_INITIAL_ITEMS_COUNT}
          withBorder
        />
      );
    }
    if (contentType === CONTENT_TYPE.RELATED_TOPICS_URL) {
      const relatedTopicsSection = section;
      const relatedTopicsUrls = compact(relatedTopicsSection.urlObjects);
      return <QuickLinks isBrowserHandled title="Related topics" links={relatedTopicsUrls} type="quickLinks" />;
    }
    if (contentType === CONTENT_TYPE.SELLING_POINT) {
      const sellingPointSection = section;
      const transformedSection = {
        ...sellingPointSection,
        sellingPointGroup: compact(sellingPointSection.sellingPointGroup),
      };
      return <Inclusions {...transformedSection} />;
    }

    if (contentType === CONTENT_TYPE.TOPIC_GROUP) {
      const topicGroupSection = section;
      const transformedSection = {
        ...topicGroupSection,
        topicGroup: compact(topicGroupSection.topicGroup),
      };
      return <Summaries {...transformedSection} />;
    }
    if (contentType === CONTENT_TYPE.AWARD) {
      const { imageObject, name, summary } = section;
      // Sanity schema enforces imageObject and name as required fields,
      // but codegen doesn't reflect schema validation (see type safety note above).
      // For now we validate at runtime to avoid crashes from corrupt or missing data.
      if (!imageObject || !name) {
        return null;
      }
      return <Award imageObject={imageObject} name={name} summaryRaw={summary} type="award" />;
    }
    if (contentType === CONTENT_TYPE.CONTENT_TILES) {
      const contentTilesSection = section;
      const tilesData = compact(contentTilesSection.tiles);
      const serviceTiles = filter((tileType) => tileType?.__typename === 'Service', tilesData);
      const contentTiles = getContentTilesData({ serviceTiles });
      return <ContentTilesSection contentTiles={contentTiles} />;
    }

    if (contentType === CONTENT_TYPE.PRODUCT_LIST) {
      const { productIdsCouple, productIdsSingle, productIdsFamily, productIdsSingleParent, name, urlObject } = section;
      const scaleToProductIdsMap = {
        [SCALE.SINGLE]: productIdsSingle ?? [],
        [SCALE.COUPLE]: productIdsCouple ?? [],
        [SCALE.FAMILY]: productIdsFamily ?? [],
        [SCALE.SINGLE_PARENT]: productIdsSingleParent ?? [],
        [SCALE.SINGLE_PARENT_PLUS_21]: productIdsSingleParent ?? [],
        [SCALE.FAMILY_PLUS_21]: productIdsFamily ?? [],
      };
      const productIds = (scaleToProductIdsMap[customerScale ?? SCALE.SINGLE] ?? []) as string[];

      const productListContent = {
        compareButtonText: urlObject?.name ?? undefined,
        compareButtonToLink: urlObject?.url ?? undefined,
        productIds,
        title: name ?? undefined,
        type: 'ProductListing',
        withBorder: true,
      };

      const sectionsContent = {
        sections: [productListContent],
      };

      return <PageSections components={{ ProductListing: ProductsSection as ElementType }} content={sectionsContent} />;
    }
    return null;
  }, content);

export default getSectionContentToRender;
